import { useState, useCallback } from "react";
import apiClient from "utils/apiClient";
import { createTaskRequest } from "services/olitaskTaskService";

const useTasks = () => {
  const authToken = sessionStorage.getItem("authToken");

  const [state, setState] = useState({
    tasks: [],
    loading: false,
    error: null,
    totalPages: 1,
    totalElements: 0,
  });

  const fetchTasks = useCallback(async (page = 0, pageSize = 10, statusFilter = "", filters = {}) => {
    if (!authToken) return;

    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      
      let url = "/olitask-service/api/v1/tasks";
      const params = new URLSearchParams({
        page,
        size: pageSize,
      });

      const {
        organizationId,
        requesterOrganizationId,
        helperOrganizationId,
        workspaceId,
        requesterWorkspaceId,
        helperWorkspaceId,
        contextRole,
        selectedWorkspaceUse,
        requester,
        helper,
      } = filters || {};
      const roleContext = String(contextRole || selectedWorkspaceUse || "").toUpperCase();
      const isRequesterContext = roleContext === "REQUESTER" || roleContext === "JOB_SEARCH";
      const isHelperContext = roleContext === "HELPER" || roleContext === "HIRING";
      const activeWorkspaceId =
        workspaceId ||
        (isRequesterContext ? requesterWorkspaceId : helperWorkspaceId) ||
        requesterWorkspaceId ||
        helperWorkspaceId;

      const hasAdvancedFilters =
        Boolean(organizationId) ||
        Boolean(requesterOrganizationId) ||
        Boolean(helperOrganizationId) ||
        Boolean(requester) ||
        Boolean(helper);

      if (isRequesterContext && activeWorkspaceId) {
        url = "/olitask-service/api/v1/tasks/requester-tasks";
        params.append("workspaceId", activeWorkspaceId);
        params.append("requesterWorkspaceId", requesterWorkspaceId || activeWorkspaceId);
      } else if (isHelperContext && activeWorkspaceId) {
        url = statusFilter
          ? "/olitask-service/api/v1/tasks/helper-tasks/status"
          : "/olitask-service/api/v1/tasks/helper-tasks";
        params.append("workspaceId", activeWorkspaceId);
        params.append("helperWorkspaceId", helperWorkspaceId || activeWorkspaceId);
        if (helperOrganizationId) {
          params.append("helperOrganizationId", helperOrganizationId);
        }
        if (statusFilter) {
          params.append("status", statusFilter);
        }
      } else if (statusFilter || hasAdvancedFilters) {
        url = "/olitask-service/api/v1/tasks/filter";
        if (statusFilter) {
          params.append("status", statusFilter);
        }
        if (requester) {
          params.append("requester", requester);
        }
        if (helper) {
          params.append("helper", helper);
        }
        if (organizationId) {
          params.append("organizationId", organizationId);
        }
        if (requesterOrganizationId) {
          params.append("requesterOrganizationId", requesterOrganizationId);
        }
        if (helperOrganizationId) {
          params.append("helperOrganizationId", helperOrganizationId);
        }
      }

      const response = await apiClient.get(`${url}?${params.toString()}`);

      console.log(response.data.content);

      let content = [];
      let totalPages = 1;
      let totalElements = 0;

      if (Array.isArray(response.data)) {
        content = response.data;
        totalElements = response.data.length;
      } else if (response.data?.content) {
        content = response.data.content || [];
        totalPages = response.data.totalPages || 1;
        totalElements = response.data.totalElements || 0;
      } else if (response.data?.data) {
        content = Array.isArray(response.data.data) ? response.data.data : [];
        totalElements = content.length;
      } else {
        content = [];
      }

      const formattedTasks = content.map(formatTaskData);

      setState({
        tasks: formattedTasks,
        loading: false,
        error: null,
        totalPages,
        totalElements,
      });
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to load tasks.";
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  }, [authToken]);

  const getTaskById = useCallback(async (taskId) => {
    if (!taskId) throw new Error("Task ID is required");
    
    setState((prev) => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await apiClient.get(`/olitask-service/api/v1/tasks/${taskId}`);
      console.log("Task details", response.data);
      const task = formatTaskData(response.data);
      setState((prev) => ({ ...prev, loading: false }));
      return task;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      throw new Error(errorMessage);
    }
  }, []);

  const createTask = useCallback(async (payload) => {
    if (!payload) throw new Error("Task payload is required");

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const task = await createTaskRequest(payload);
      setState((prev) => ({ ...prev, loading: false }));
      return formatTaskData(task);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to create task.";
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
      throw new Error(errorMessage);
    }
  }, []);

  const updateTaskStatus = useCallback(async (taskId, status, page = 0, pageSize = 10, statusFilter = "", filters = {}) => {
    if (!taskId) throw new Error("Task ID is required");
    
    try {
      await apiClient.put(`/olitask-service/api/v1/tasks/${taskId}/status`, { status });
      await fetchTasks(page, pageSize, statusFilter, filters);
    } catch (err) {
      throw err;
    }
  }, [fetchTasks]);

  const deactivateTask = useCallback(async (taskId, page = 0, pageSize = 10, statusFilter = "", filters = {}) => {
    if (!taskId) throw new Error("Task ID is required");
    
    try {
      await apiClient.put(`/olitask-service/api/v1/tasks/${taskId}/deactivate`);
      await fetchTasks(page, pageSize, statusFilter, filters);
    } catch (err) {
      throw err;
    }
  }, [fetchTasks]);

  const deleteTask = useCallback(async (taskId, page = 0, pageSize = 10, statusFilter = "", filters = {}) => {
    if (!taskId) throw new Error("Task ID is required");
    
    try {
      await apiClient.delete(`/olitask-service/api/v1/tasks/${taskId}?type=SOFT`);
      await fetchTasks(page, pageSize, statusFilter, filters);
    } catch (err) {
      throw err;
    }
  }, [fetchTasks]);



  const { tasks, loading, error, totalPages, totalElements } = state;

  const getAllCounterOffers = useCallback(() => {
    return tasks.flatMap((task) =>
      (task.counterOffers || []).map((co) => ({
        ...co,
        taskId: task.id,
        taskTitle: task.title,
        taskStatus: task.status,
        requester: task.requester,
      }))
    );
  }, [tasks]);

  return {
    tasks,
    loading,
    error,
    totalPages,
    totalElements,
    fetchTasks,
    getTaskById,
    createTask,
    updateTaskStatus,
    deactivateTask,
    deleteTask,
    totalTasks: totalElements,
    completedTasks: tasks.filter((t) => t.status === "COMPLETED").length,
    activeTasks: tasks.filter((t) => t.status === "ACTIVE").length,
    pendingTasks: tasks.filter((t) => t.status === "PENDING").length,
    getAllCounterOffers
  };
};

function formatTaskData(task = {}) {
  return {
    ...task,
    id: task.id ?? null,
    title: task.title ?? "Untitled",
    description: task.description ?? "",
    status: task.status ?? "UNKNOWN",
    priority: task.priority ?? 0,
    assignedTo: task.assignedTo ?? null,
    requester: task.requester ?? null,
    helper: task.helper ?? null,
    taskCharges: task.taskCharges ?? {},
    createdAt: task.createdAt ?? null,
    updatedAt: task.updatedAt ?? null,
    photos: Array.isArray(task.photos) ? task.photos : [],
    location: task.location ?? {},
    counterOffers: Array.isArray(task.counterOffers)
      ? task.counterOffers.map(formatCounterOffer)
      : [],
  };
}

function formatCounterOffer(co = {}) {
  return {
    ...co,
    id: co.id,

    // helper info
    helper: co.helper,

    // original offer
    amount: co.amount,
    durationMinutes: co.durationMinutes,
    reason: co.reason,
    submittedAt: co.submittedAt,

    // status flow
    status: co.status,
    responseStatus: co.responseStatus,

    // response data
    responseAmount: co.responseAmount,
    responseDurationMinutes: co.responseDurationMinutes,
    responseReason: co.responseReason,
    responseAt: co.responseAt,

    // engagement
    isStillInterested: co.isStillInterested,
  };
}

export default useTasks;
